import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileText, ArrowRight, Calendar, Eye, Newspaper } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import AmbientBackground from '../common/AmbientBackground';

const LatestNews = () => {
  const { isDarkMode } = useTheme();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await fetch(
          'https://api.fsgu.co.za/get-posts.php?status=published&limit=3'
        );
        const data = await res.json();
        if (mounted && data.success && data.data) {
          setPosts(data.data);
        }
      } catch (err) {
        console.error('Latest news load error:', err);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const stripHtml = (html) => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '').slice(0, 140);
  };

  return (
    <section className="py-16 sm:py-20 lg:py-28 relative overflow-hidden">
      <AmbientBackground variant="green" />

      <div className="container-custom relative">
        {/* Centered header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10 sm:mb-14"
        >
          <div className="inline-flex items-center justify-center gap-2 mb-4">
            <div className={`h-px w-8 ${isDarkMode ? 'bg-emerald-400/60' : 'bg-emerald-500/60'}`} />
            <span className={`text-[10px] sm:text-xs font-semibold uppercase tracking-[0.3em] ${
              isDarkMode ? 'text-emerald-400' : 'text-emerald-600'
            }`}>
              Latest News
            </span>
            <div className={`h-px w-8 ${isDarkMode ? 'bg-emerald-400/60' : 'bg-emerald-500/60'}`} />
          </div>
          <h2 className={`text-3xl sm:text-4xl lg:text-5xl font-bold font-cursive mb-3 ${
            isDarkMode ? 'text-white' : 'text-slate-900'
          }`}>
            Stay in the loop
          </h2>
          <p className={`text-base sm:text-lg mb-6 ${
            isDarkMode ? 'text-slate-400' : 'text-slate-600'
          }`}>
            Announcements, tournament updates, and stories from the clubs
          </p>
          <Link
            to="/news"
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold backdrop-blur-xl border transition-all duration-300 hover:scale-[1.02] ${
              isDarkMode
                ? 'bg-white/[0.05] border-white/10 text-emerald-400 hover:bg-white/[0.08]'
                : 'bg-white/60 border-white/80 text-emerald-600 hover:bg-white/80'
            }`}
          >
            <Newspaper className="w-4 h-4" />
            View all news
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </motion.div>

        {loading ? (
          <div className="h-48 sm:h-64 flex items-center justify-center">
            <div className={`inline-block animate-spin rounded-full h-10 w-10 border-4 border-solid border-current border-r-transparent ${
              isDarkMode ? 'text-emerald-400' : 'text-emerald-500'
            }`} />
          </div>
        ) : posts.length === 0 ? (
          <div className={`text-center py-12 sm:py-16 rounded-3xl backdrop-blur-xl ${
            isDarkMode ? 'bg-white/[0.04] border border-white/[0.08]' : 'bg-white/60 border border-white/80'
          }`}>
            <FileText className={`w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 ${
              isDarkMode ? 'text-slate-600' : 'text-slate-400'
            }`} />
            <p className={`text-sm sm:text-base ${
              isDarkMode ? 'text-slate-400' : 'text-slate-600'
            }`}>
              No news posts yet. Check back soon!
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5 max-w-md md:max-w-none mx-auto">
            {posts.map((post, index) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{ duration: 0.6, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="relative group"
              >
                {/* Hover glow */}
                <div className="absolute -inset-0.5 rounded-3xl bg-gradient-to-br from-emerald-500/0 via-emerald-500/0 to-emerald-500/0 group-hover:from-emerald-500/20 group-hover:via-emerald-400/10 group-hover:to-amber-500/10 blur-xl opacity-0 group-hover:opacity-100 transition-all duration-700" />

                <Link
                  to={`/news/${post.slug || post.id}`}
                  className={`relative block h-full rounded-2xl sm:rounded-3xl overflow-hidden transition-all duration-500 group-hover:-translate-y-1 backdrop-blur-xl ${
                    isDarkMode
                      ? 'bg-white/[0.04] border border-white/[0.08] group-hover:border-emerald-400/30'
                      : 'bg-white/60 border border-white/80 group-hover:border-emerald-400/50 shadow-lg'
                  }`}
                >
                  {/* Top shine */}
                  <div
                    className="absolute inset-x-0 top-0 h-px z-10"
                    style={{
                      background: isDarkMode
                        ? 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)'
                        : 'linear-gradient(90deg, transparent, rgba(255,255,255,0.95), transparent)'
                    }}
                  />

                  {post.featured_image ? (
                    <div className="relative h-44 sm:h-52 overflow-hidden">
                      <img
                        src={post.featured_image}
                        alt={post.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                      {post.category_name && (
                        <div className="absolute top-3 left-3">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] sm:text-xs font-semibold text-white backdrop-blur-xl bg-black/40 border border-white/20">
                            {post.category_name}
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="relative h-44 sm:h-52 flex items-center justify-center bg-gradient-to-br from-emerald-500/10 to-amber-500/5">
                      <FileText className={`w-10 h-10 sm:w-12 sm:h-12 ${
                        isDarkMode ? 'text-emerald-500/40' : 'text-emerald-500/50'
                      }`} />
                    </div>
                  )}

                  <div className="p-5 sm:p-6">
                    <h3 className={`text-base sm:text-lg font-bold mb-2 line-clamp-2 group-hover:text-emerald-500 transition-colors ${
                      isDarkMode ? 'text-white' : 'text-slate-900'
                    }`}>
                      {post.title}
                    </h3>

                    <p className={`text-xs sm:text-sm mb-4 line-clamp-2 leading-relaxed ${
                      isDarkMode ? 'text-slate-400' : 'text-slate-600'
                    }`}>
                      {post.excerpt || stripHtml(post.content)}
                    </p>

                    <div className={`flex items-center justify-between text-[10px] sm:text-xs pt-4 border-t ${
                      isDarkMode
                        ? 'text-slate-500 border-white/[0.08]'
                        : 'text-slate-500 border-slate-900/[0.06]'
                    }`}>
                      <div className="flex items-center gap-1.5">
                        <Calendar className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                        {formatDate(post.published_at || post.created_at)}
                      </div>
                      {post.views > 0 && (
                        <div className="flex items-center gap-1.5">
                          <Eye className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                          {post.views}
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default LatestNews;